import {
  ArrowRight,
  Gear,
  House,
  type Icon as PhosphorIcon,
  Pulse,
  SignOut,
  Users,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router";

import { useHealth, useLogout, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

interface HubRowProps {
  to: string;
  icon: PhosphorIcon;
  title: string;
  description: string;
}

/**
 * The same link-row pattern the splash screen uses: a flat tinted block, the
 * icon bare on the tint rather than boxed in a tile of its own, and the arrow
 * at the far right. No border — the block colour does that job.
 */
function HubRow({ to, icon: Icon, title, description }: HubRowProps) {
  return (
    <Link
      to={to}
      className="bg-daylight-subtle hover:bg-daylight-soft focus-visible:outline-ring group flex items-center gap-4 rounded-sm p-5 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <Icon weight="light" className="text-primary size-7 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-primary text-[17px] leading-6 font-medium">{title}</p>
        <p className="text-muted text-[13px] leading-4">{description}</p>
      </div>
      <ArrowRight weight="light" className="text-primary size-5 shrink-0" aria-hidden />
    </Link>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";
  const backendOk = backendStatus === "ok";
  const isAdmin = me?.role === "admin";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border border-b bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <div className="flex flex-col justify-center gap-0.5">
            <span className="type-label text-muted">VELUX</span>
            <span className="text-primary text-[15px] leading-4 font-semibold">Smart Climate</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-muted hover:text-primary hover:bg-background flex items-center gap-2 rounded-md px-3 py-1.5 text-[15px] font-medium transition-colors"
          >
            <SignOut weight="light" className="size-4" aria-hidden />
            Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto flex max-w-4xl flex-col gap-8 px-6 py-12">
        <div className="flex flex-col gap-3">
          <p className="type-label text-muted">{isAdmin ? "VELUX team" : "Your account"}</p>
          <h1 className="text-primary text-[32px] leading-10 font-semibold tracking-[-0.32px]">
            {me?.first_name ? `Welcome back, ${me.first_name}.` : "Welcome back."}
          </h1>
          <p className="text-muted text-[17px] leading-6">
            {isAdmin
              ? "Manage households, users, and monitor Smart Climate setup progress."
              : "Manage your profile and review your recent activity."}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {isAdmin && (
            <>
              <HubRow
                to="/admin/households"
                icon={House}
                title="Households"
                description="Provision and track Smart Climate installs"
              />
              <HubRow
                to="/admin/users"
                icon={Users}
                title="Users"
                description="Invite and manage team members"
              />
              <HubRow
                to="/admin/activity"
                icon={Pulse}
                title="Activity"
                description="Monitor events and setup analytics"
              />
            </>
          )}
          <HubRow
            to="/profile"
            icon={Gear}
            title="Profile"
            description="Update your name, password, and details"
          />
        </div>

        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-[13px] font-semibold",
              backendOk ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive",
            )}
          >
            <span
              className={cn(
                "inline-block size-1.5 rounded-full",
                backendOk ? "bg-success" : "bg-destructive",
              )}
            />
            Backend {backendStatus}
          </span>
        </div>
      </main>
    </div>
  );
}
